import React, { memo } from 'react';
import { Close, LocationOn } from '@mui/icons-material';
import VerifiedBadge from 'components/VerifiedBadge';
import { UserRound, Ruler, Eye, Heart, CigaretteOff } from 'lucide-react';

import generateValidUrl from 'core/fetch/generateValidUrl';
import isNullOrUndefined from 'core/typeguards/isNullOrUndefined';
import { userTraits } from 'config/traits';
import { IImage } from 'models/core/image/IImage';
import { getAge } from 'models/user/IUser';
import { IStrangerUser } from 'models/user/IStrangerUser/IStrangerUser';
import useTranslation from 'services/i18n/core/useTranslation';
import {
    AugenMap,
    BeziehungMap,
    HaareMap,
    SmokerTraitMap,
} from 'temp/models/BerndUserTraits';
import hairIcon from '../../../../assets/images/icons/hair.png';
import FSK18 from '../../../../assets/images/icons/fsk18.png';
import StrangerProfileAttributeRow from './StrangerProfileAttributeRow';
import { parseJsonTags } from '../utils/parseJsonTags';

const countryLabels: Record<string, string> = {
    de: 'Deutschland',
    at: 'Österreich',
    ch: 'Schweiz',
};

const getCountryLabel = (country?: string) => {
    if (!country) {
        return 'Deutschland';
    }

    return countryLabels[country.toLowerCase()] || country;
};

export interface StrangerProfileInfoCardProps {
    strangerUser: IStrangerUser;
    token?: string;
    onClose: () => void;
    blurLocation?: boolean;
    pictures?: IImage[];
    onPictureClick?: (index: number) => void;
}

export const StrangerProfileInfoCard = memo(
    ({ strangerUser, token, onClose, blurLocation = false, pictures = [], onPictureClick }: StrangerProfileInfoCardProps) => {
    const translation = useTranslation();
    const {
        Username,
        Birthday,
        City,
        Verified,
        UeberMich,
        Interessen,
        Size,
        Hair,
        Eyes,
        Relationship,
        Smoker,
    } = strangerUser;

    const characterTags = parseJsonTags((strangerUser as IStrangerUser & { Charakter?: string }).Charakter);
    const interestTags = parseJsonTags(Interessen);
    const figureIndex = (strangerUser as IStrangerUser & { Figure?: number }).Figure;
    const figureLabel = figureIndex ? userTraits.figure[figureIndex - 1] : undefined;

    const country = getCountryLabel((strangerUser as IStrangerUser & { Country?: string }).Country);
    const locationText = City ? `${City}, ${country}` : country;
    const verifiedIcon = Verified === 1 ? <VerifiedBadge className="stranger-profile__verified" size={20} /> : null;

    const attributes = [
        figureLabel && {
            icon: <UserRound size={20} strokeWidth={2} />,
            label: 'Körperbau',
            value: figureLabel,
        },
        !isNullOrUndefined(Size) && Size !== 0 && {
            icon: <Ruler size={20} strokeWidth={2} />,
            label: translation.TRAITS_SIZE_TITLE,
            value: `${Size} cm`,
        },
        !isNullOrUndefined(Hair) && Hair !== 0 && {
            icon: <img src={hairIcon} alt="" />,
            label: translation.TRAITS_HAIRCOLOR_TITLE,
            value: translation[HaareMap[Hair] as keyof typeof translation] as string,
        },
        !isNullOrUndefined(Eyes) && Eyes !== 0 && {
            icon: <Eye size={20} strokeWidth={2} />,
            label: translation.TRAITS_EYECOLOR_TITLE,
            value: translation[AugenMap[Eyes] as keyof typeof translation] as string,
        },
        !isNullOrUndefined(Relationship) && Relationship !== 0 && {
            icon: <Heart size={20} strokeWidth={2} />,
            label: translation.TRAITS_RELATIONSHIP_TITLE,
            value: translation[BeziehungMap[Relationship] as keyof typeof translation] as string,
        },
        !isNullOrUndefined(Smoker) && Smoker !== 0 && {
            icon: <CigaretteOff size={20} strokeWidth={2} />,
            label: translation.TRAITS_SMOKER_TITLE,
            value: translation[SmokerTraitMap[Smoker] as keyof typeof translation] as string,
        },
    ].filter(Boolean) as Array<{ icon: React.ReactNode; label: string; value: string }>;

    return (
        <article className="stranger-profile__card">
            <div className="stranger-profile__card-handle" aria-hidden="true">
                <span className="stranger-profile__card-handle-bar" />
            </div>

            <button type="button" className="stranger-profile__close-btn" onClick={onClose} aria-label="Close profile">
                <Close sx={{ fontSize: 18 }} />
            </button>

            <header className="stranger-profile__header">
                <h1 className="stranger-profile__name">
                    {Username}, {getAge(Birthday || '')}
                    {verifiedIcon}
                </h1>
                <p className="stranger-profile__location">
                    <LocationOn className="stranger-profile__location-icon" sx={{ fontSize: 18 }} />
                    {token && !blurLocation ? locationText : <span style={{ filter: 'blur(6px)' }}>Registrier dich!</span>}
                </p>
            </header>

            {UeberMich && (
                <section className="stranger-profile__section">
                    <h2 className="stranger-profile__section-title">{translation.PROFILE_ABOUT_TITLE}:</h2>
                    <p className="stranger-profile__about-text">{UeberMich}</p>
                </section>
            )}

            {characterTags.length > 0 && (
                <section className="stranger-profile__section">
                    <h2 className="stranger-profile__section-title">Charakter</h2>
                    <div className="stranger-profile__tags">
                        {characterTags.map((tag) => (
                            <span key={tag} className="stranger-profile__tag">
                                {tag}
                            </span>
                        ))}
                    </div>
                </section>
            )}

            {interestTags.length > 0 && (
                <section className="stranger-profile__section">
                    <h2 className="stranger-profile__section-title">{translation.PROFILE_INTERESTS_TITLE}</h2>
                    <div className="stranger-profile__tags">
                        {interestTags.map((tag) => (
                            <span key={tag} className="stranger-profile__tag">
                                {tag}
                            </span>
                        ))}
                    </div>
                </section>
            )}

            {attributes.length > 0 && (
                <section className="stranger-profile__section">
                    <div className="stranger-profile__attributes">
                        {attributes.map((attribute) => (
                            <StrangerProfileAttributeRow
                                key={attribute.label}
                                icon={attribute.icon}
                                label={attribute.label}
                                value={attribute.value}
                            />
                        ))}
                    </div>
                </section>
            )}

            {pictures.length > 0 && (
                <section className="stranger-profile__section">
                    <h2 className="stranger-profile__section-title">Bilder</h2>
                    <div className="stranger-profile__gallery">
                        {pictures.map((image, index) => {
                            const isLocked = (image.Coins ?? 0) > 0;

                            return (
                                <button
                                    type="button"
                                    key={`${image.Picture}-${index}`}
                                    className={`stranger-profile__gallery-item${isLocked ? ' stranger-profile__gallery-item--locked' : ''}`}
                                    onClick={() => onPictureClick?.(index)}
                                >
                                    <img
                                        className="stranger-profile__gallery-image"
                                        src={generateValidUrl(image.Picture)}
                                        alt={`${Username} ${index + 1}`}
                                        loading="lazy"
                                    />
                                    {isLocked && (
                                        <span className="stranger-profile__gallery-lock">
                                            <img src={FSK18} alt="" />
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}
        </article>
    );
});

export default StrangerProfileInfoCard;
